import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Page, BlockStack } from "@shopify/polaris";
import { APP_DISPLAY_NAME, PARTNER_APP_ID } from "../config/appBranding";
import { useAppTitleBar } from "../hooks/useAppTitleBar";
import { useEnsureInstall } from "../hooks/useEnsureInstall";
import { useThemeEmbedStatus } from "../hooks/useThemeEmbedStatus";
import { useAgeShieldOnboarding } from "../hooks/useAgeShieldOnboarding";
import { useSyncShopMetafieldsAfterInstall } from "../hooks/useSyncShopMetafieldsAfterInstall";
import SetupGuideSection from "../components/dashboard/SetupGuideSection";
import ThemeAppEmbedStatusBanner from "../components/dashboard/ThemeAppEmbedStatusBanner";
import AnalyticsSection from "../components/dashboard/AnalyticsSection";
import FaqSection from "../components/dashboard/FaqSection";
import RecommendedAppsSection from "../components/dashboard/RecommendedAppsSection";
import { getStorefrontUrl } from "../utils/shopDomain";

export default function Dashboard() {
  const navigate = useNavigate();
  const { shop, installChecked, installData } = useEnsureInstall();
  const shopDomain = shop || "";

  useSyncShopMetafieldsAfterInstall(installChecked, installData);

  const onGoCreateTemplate = useCallback(() => {
    navigate("/settings/templates/new");
  }, [navigate]);

  const {
    themeLoading,
    themeRefreshing,
    embedInactive,
    themeCheckError,
    refresh,
    getThemeEditorDeepLink,
  } = useThemeEmbedStatus(shopDomain);

  const onboarding = useAgeShieldOnboarding({
    installChecked,
    installData,
    themeLoading,
    embedInactive,
  });

  const storefrontUrl = useMemo(() => getStorefrontUrl(shopDomain), [shopDomain]);

  useAppTitleBar({ title: APP_DISPLAY_NAME });

  const setupProps = useMemo(
    () => ({
      themeLoading,
      themeRefreshing,
      embedInactive,
      themeCheckError,
      onRefreshThemes: refresh,
      getThemeEditorDeepLink,
      storefrontUrl,
      onDismissSetupGuide: onboarding.dismissSetupGuide,
      templatesChecked: onboarding.templatesChecked,
      step1Done: onboarding.step1Done,
      step2Done: onboarding.step2Done,
      step3Done: onboarding.step3Done,
      completedCount: onboarding.completedCount,
      onGoCreateTemplate,
      onMarkTemplate: onboarding.markTemplateStep,
      onMarkSetupComplete: onboarding.markSetupComplete,
    }),
    [
      themeLoading,
      themeRefreshing,
      embedInactive,
      themeCheckError,
      refresh,
      getThemeEditorDeepLink,
      storefrontUrl,
      onboarding.dismissSetupGuide,
      onboarding.templatesChecked,
      onboarding.step1Done,
      onboarding.step2Done,
      onboarding.step3Done,
      onboarding.completedCount,
      onGoCreateTemplate,
      onboarding.markTemplateStep,
      onboarding.markSetupComplete,
    ],
  );

  return (
    <Page>
      <BlockStack gap="500">
        {!onboarding.setupGuideDismissed ? (
          <SetupGuideSection {...setupProps} />
        ) : (
          <ThemeAppEmbedStatusBanner
            themeLoading={themeLoading}
            themeRefreshing={themeRefreshing}
            embedInactive={embedInactive}
            themeCheckError={themeCheckError}
            onRefreshThemes={refresh}
            getThemeEditorDeepLink={getThemeEditorDeepLink}
          />
        )}
        <AnalyticsSection />
        <FaqSection />
        <RecommendedAppsSection currentAppId={PARTNER_APP_ID} />
      </BlockStack>
    </Page>
  );
}
